import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import retailBillService, { RetailBill } from '../../services/retailBillService';
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

const ViewRetailBill: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [bill, setBill] = useState<RetailBill | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState<{
    paymentStatus: 'paid' | 'unpaid' | 'partial';
    paymentMethod: 'cash' | 'card' | 'upi' | 'net_banking' | 'wallet' | '';
    paidAmount: number;
    paymentReference: string;
  }>({
    paymentStatus: 'unpaid',
    paymentMethod: '',
    paidAmount: 0,
    paymentReference: '',
  });
  const [newPayment, setNewPayment] = useState<{
    amount: number;
    paymentMethod: 'cash' | 'card' | 'upi' | 'net_banking' | 'wallet' | '';
    paymentReference: string;
    notes: string;
  }>({
    amount: 0,
    paymentMethod: '',
    paymentReference: '',
    notes: '',
  });

  useEffect(() => {
    if (id) {
      fetchBill();
    }
  }, [id]);

  const fetchBill = async () => {
    try {
      setLoading(true);
      const data = await retailBillService.getById(id!);

      // Check if bill has paidAmount but no payment logs (legacy bills)
      // This is a one-time sync for old bills created before payment logging was implemented
      if (data.paidAmount && data.paidAmount > 0 &&
          (!data.paymentLogs || data.paymentLogs.length === 0) &&
          data.paymentMethod) {
        try {
          // Create initial payment log for legacy bill
          await retailBillService.addPayment(id!, {
            amount: data.paidAmount,
            paymentMethod: data.paymentMethod,
            paymentReference: data.paymentReference || '',
            notes: 'Initial payment (auto-synced from legacy bill)',
          });
          // Refetch to get the updated bill with payment log
          const updatedData = await retailBillService.getById(id!);
          setBill(updatedData);
          setPaymentData({
            paymentStatus: updatedData.paymentStatus || 'unpaid',
            paymentMethod: updatedData.paymentMethod || '',
            paidAmount: updatedData.paidAmount || 0,
            paymentReference: updatedData.paymentReference || '',
          });
          return;
        } catch (syncError) {
          console.error('Failed to sync legacy payment log:', syncError);
          // Continue with original data if sync fails
        }
      }

      setBill(data);
      setPaymentData({
        paymentStatus: data.paymentStatus || 'unpaid',
        paymentMethod: data.paymentMethod || '',
        paidAmount: data.paidAmount || 0,
        paymentReference: data.paymentReference || '',
      });
    } catch (error) {
      console.error('Error fetching bill:', error);
      alert('Failed to load bill details');
      navigate('/retail-bills');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!bill) return;
    try {
      const blob = await retailBillService.downloadPDF(bill._id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${bill.billNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF');
    }
  };

  const handleUpdatePayment = async () => {
    if (!id || !bill) return;

    try {
      setUpdating(true);

      // Check if there's a payment increase - if yes, log it as a new payment
      const currentPaid = bill.paidAmount || 0;
      const newPaid = paymentData.paidAmount;
      const paymentIncrease = newPaid - currentPaid;

      if (paymentIncrease > 0 && paymentData.paymentMethod) {
        // Create a new payment log for the increase
        await retailBillService.addPayment(id, {
          amount: paymentIncrease,
          paymentMethod: paymentData.paymentMethod as 'cash' | 'card' | 'upi' | 'net_banking' | 'wallet',
          paymentReference: paymentData.paymentReference,
          notes: `Payment update - increasing from ${formatCurrency(currentPaid)} to ${formatCurrency(newPaid)}`,
        });
      } else {
        // If no payment increase, just update the status/reference
        const updateData: any = {
          paymentStatus: paymentData.paymentStatus,
          paymentReference: paymentData.paymentReference,
        };

        // Only include paymentMethod if it's not empty string
        if (paymentData.paymentMethod) {
          updateData.paymentMethod = paymentData.paymentMethod;
        }

        await retailBillService.update(id, updateData);
      }

      setShowPaymentModal(false);
      fetchBill();
    } catch (error) {
      console.error('Error updating payment:', error);
      alert('Failed to update payment information');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddPayment = async () => {
    if (!id) return;
    if (!newPayment.paymentMethod || newPayment.amount <= 0) {
      alert('Please provide payment method and valid amount');
      return;
    }
    try {
      setUpdating(true);
      await retailBillService.addPayment(id, {
        amount: newPayment.amount,
        paymentMethod: newPayment.paymentMethod as 'cash' | 'card' | 'upi' | 'net_banking' | 'wallet',
        paymentReference: newPayment.paymentReference,
        notes: newPayment.notes,
      });
      setShowAddPaymentModal(false);
      setNewPayment({
        amount: 0,
        paymentMethod: '',
        paymentReference: '',
        notes: '',
      });
      fetchBill();
    } catch (error) {
      console.error('Error adding payment:', error);
      alert('Failed to add payment');
    } finally {
      setUpdating(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPaymentStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircleIcon className="h-6 w-6 text-green-600" />;
      case 'unpaid':
        return <XCircleIcon className="h-6 w-6 text-red-600" />;
      case 'partial':
        return <ClockIcon className="h-6 w-6 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getPaymentStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      paid: 'bg-green-100 text-green-800',
      unpaid: 'bg-red-100 text-red-800',
      partial: 'bg-yellow-100 text-yellow-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Bill not found</p>
        <Link to="/retail-bills" className="mt-4 text-indigo-600 hover:text-indigo-700">
          Back to Bills
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/retail-bills')}
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{bill.billNumber}</h1>
            <p className="mt-1 text-sm text-gray-600">
              Created on {new Date(bill.createdAt).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          {bill.paymentStatus !== 'paid' && (
            <button
              onClick={() => setShowAddPaymentModal(true)}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Payment
            </button>
          )}
          <button
            onClick={() => setShowPaymentModal(true)}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <PencilSquareIcon className="h-5 w-5 mr-2" />
            Update Payment
          </button>
          <button
            onClick={handleDownloadPDF}
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            Download PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium text-gray-900">{bill.customerName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-medium text-gray-900">{bill.customerPhone}</p>
              </div>
              {bill.customerEmail && (
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium text-gray-900">{bill.customerEmail}</p>
                </div>
              )}
              {bill.customerAddress && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Address</p>
                  <p className="font-medium text-gray-900">{bill.customerAddress}</p>
                </div>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Items</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unit Price
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Discount
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tax
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bill.items.map((item, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{item.itemName}</div>
                        <div className="text-sm text-gray-500">{item.itemCode}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {item.quantity} {item.unit}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right">
                        {formatCurrency(item.unitPrice)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right">
                        {item.discount ? formatCurrency(item.discount) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right">
                        {item.tax ? formatCurrency(item.tax) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 text-right">
                        {formatCurrency(item.totalPrice)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes */}
          {bill.notes && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{bill.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Payment Status */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Status</h2>
            <div className="flex items-center space-x-3 mb-4">
              {bill.paymentStatus && getPaymentStatusIcon(bill.paymentStatus)}
              <span
                className={`px-3 py-1 text-sm font-semibold rounded-full ${getPaymentStatusColor(
                  bill.paymentStatus || 'unpaid'
                )}`}
              >
                {bill.paymentStatus ? bill.paymentStatus.charAt(0).toUpperCase() + bill.paymentStatus.slice(1) : 'Unknown'}
              </span>
            </div>
            {bill.paymentMethod && (
              <div className="mb-3">
                <p className="text-sm text-gray-600">Payment Method</p>
                <p className="font-medium text-gray-900 capitalize">{bill.paymentMethod.replace('_', ' ')}</p>
              </div>
            )}
            {bill.paymentReference && (
              <div className="mb-3">
                <p className="text-sm text-gray-600">Reference</p>
                <p className="font-medium text-gray-900">{bill.paymentReference}</p>
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Bill Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium text-gray-900">{formatCurrency(bill.subtotal)}</span>
              </div>
              {bill.totalDiscount !== undefined && bill.totalDiscount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Discount</span>
                  <span className="font-medium text-red-600">-{formatCurrency(bill.totalDiscount)}</span>
                </div>
              )}
              {bill.totalTax !== undefined && bill.totalTax > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Tax</span>
                  <span className="font-medium text-gray-900">{formatCurrency(bill.totalTax)}</span>
                </div>
              )}
              <div className="border-t pt-3">
                <div className="flex justify-between">
                  <span className="text-base font-semibold text-gray-900">Grand Total</span>
                  <span className="text-lg font-bold text-indigo-600">{formatCurrency(bill.grandTotal)}</span>
                </div>
              </div>
              {bill.paidAmount !== undefined && bill.paidAmount > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Paid Amount</span>
                    <span className="font-medium text-green-600">{formatCurrency(bill.paidAmount)}</span>
                  </div>
                  {bill.paymentStatus === 'partial' && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Balance Due</span>
                      <span className="font-medium text-red-600">
                        {formatCurrency(bill.grandTotal - bill.paidAmount)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Payment Logs */}
          {((bill.paymentLogs && bill.paymentLogs.length > 0) || (bill.paidAmount && bill.paidAmount > 0)) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment History</h2>
              <div className="space-y-3">
                {bill.paymentLogs && bill.paymentLogs.length > 0 ? (
                  bill.paymentLogs.map((log) => (
                    <div key={log._id} className="border-l-4 border-indigo-600 pl-4 py-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">{formatCurrency(log.amount)}</p>
                          <p className="text-sm text-gray-600 capitalize">
                            via {log.paymentMethod.replace('_', ' ')}
                          </p>
                          {log.paymentReference && (
                            <p className="text-xs text-gray-500">Ref: {log.paymentReference}</p>
                          )}
                          {log.notes && (
                            <p className="text-xs text-gray-600 mt-1">{log.notes}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">
                            {new Date(log.createdAt).toLocaleDateString('en-IN')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(log.createdAt).toLocaleTimeString('en-IN', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="border-l-4 border-yellow-500 pl-4 py-3 bg-yellow-50">
                    <div>
                      <p className="font-medium text-gray-900">{formatCurrency(bill.paidAmount || 0)}</p>
                      <p className="text-sm text-gray-600 capitalize">
                        via {bill.paymentMethod?.replace('_', ' ') || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Initial payment (created with bill)</p>
                      <p className="text-xs text-gray-500">
                        {new Date(bill.createdAt).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Update Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Update Payment Information</h3>
              <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Grand Total:</span>
                  <span className="font-medium text-gray-900">{formatCurrency(bill.grandTotal)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-600">Currently Paid:</span>
                  <span className="font-medium text-green-600">{formatCurrency(bill.paidAmount || 0)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1 pt-2 border-t border-blue-200">
                  <span className="text-gray-600">Remaining:</span>
                  <span className="font-medium text-red-600">{formatCurrency(bill.grandTotal - (bill.paidAmount || 0))}</span>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Status
                </label>
                <select
                  value={paymentData.paymentStatus}
                  onChange={(e) => setPaymentData({ ...paymentData, paymentStatus: e.target.value as 'paid' | 'unpaid' | 'partial' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="partial">Partial</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method
                </label>
                <select
                  value={paymentData.paymentMethod}
                  onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value as 'cash' | 'card' | 'upi' | 'net_banking' | 'wallet' | '' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select Method</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="upi">UPI</option>
                  <option value="net_banking">Net Banking</option>
                  <option value="wallet">Wallet</option>
                </select>
              </div>
              {paymentData.paymentStatus !== 'unpaid' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Paid Amount
                  </label>
                  <input
                    type="number"
                    value={paymentData.paidAmount}
                    onChange={(e) => setPaymentData({ ...paymentData, paidAmount: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    min="0"
                    max={bill.grandTotal}
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Reference (optional)
                </label>
                <input
                  type="text"
                  value={paymentData.paymentReference}
                  onChange={(e) => setPaymentData({ ...paymentData, paymentReference: e.target.value })}
                  placeholder="Transaction ID, Check number, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              {paymentData.paidAmount > (bill.paidAmount || 0) && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800">
                    ℹ️ <strong>Payment will be logged:</strong> A new payment entry of {formatCurrency(paymentData.paidAmount - (bill.paidAmount || 0))} will be added to payment history.
                  </p>
                </div>
              )}
            </div>
            <div className="mt-6 flex space-x-3">
              <button
                onClick={handleUpdatePayment}
                disabled={updating}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {updating ? 'Updating...' : 'Update'}
              </button>
              <button
                onClick={() => setShowPaymentModal(false)}
                disabled={updating}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      {showAddPaymentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Payment</h3>
              <p className="text-sm text-gray-600 mt-1">
                Remaining Balance: <span className="font-medium text-red-600">
                  {formatCurrency(bill.grandTotal - (bill.paidAmount || 0))}
                </span>
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Amount *
                </label>
                <input
                  type="number"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({ ...newPayment, amount: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method *
                </label>
                <select
                  value={newPayment.paymentMethod}
                  onChange={(e) => setNewPayment({ ...newPayment, paymentMethod: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select Method</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="upi">UPI</option>
                  <option value="net_banking">Net Banking</option>
                  <option value="wallet">Wallet</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Reference (Optional)
                </label>
                <input
                  type="text"
                  value={newPayment.paymentReference}
                  onChange={(e) => setNewPayment({ ...newPayment, paymentReference: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Transaction ID, Cheque No., etc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={newPayment.notes}
                  onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Additional notes"
                  rows={2}
                />
              </div>
            </div>
            <div className="mt-6 flex space-x-3">
              <button
                onClick={handleAddPayment}
                disabled={updating}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {updating ? 'Adding...' : 'Add Payment'}
              </button>
              <button
                onClick={() => {
                  setShowAddPaymentModal(false);
                  setNewPayment({
                    amount: 0,
                    paymentMethod: '',
                    paymentReference: '',
                    notes: '',
                  });
                }}
                disabled={updating}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewRetailBill;
